import React, { useRef, useState } from "react";
import { Button, Dropdown, Form, Grid, Input, Segment } from "semantic-ui-react";

import Page from "../Page";
import { toast } from "../../common/Toast";
import LocalStorage from "../../../lib/LocalStorage";
import config from "../../../common/repository/config";
import constants from "../../../common/constants";

interface Client {
    _id: string;
    name: string;
    type: string;
    case_type_id: string;
}

interface ClientDataToAdd {
    ClientId: string;
    CaseType: string;
}

interface UploadPayload {
    caseTypeName: string;
    treatmentType: string;
    clientType: string;
    clientsDataToAdd: ClientDataToAdd[];
    tpaCompanyIds?: string[];
}

interface ClientApiResponse {
    success: boolean;
    message?: string;
    data?: {
        items?: Client[];
    };
}

const BulkUpload = () => {
    const [loading, setLoading] = useState(false);
    const [caseTypeName, setCaseTypeName] = useState("");
    const [clients, setClients] = useState<Client[]>([]);
    const [tpaCompany, setTpaCompany] = useState<Client[]>([]);
    const [clientIds, setClientIds] = useState<string[]>([]);
    const [tpaCompanyIds, setTpaCompanyIds] = useState<string[]>([]);
    const [treatmentType, setTreatmentType] = useState("");
    const [clientType, setClientType] = useState("");
    const [file, setFile] = useState<File | null>(null);

    const fileRef = useRef<HTMLInputElement>(null);

    const treatmentTypeOptions = [
        {
            key: constants.treatmentType.surgical,
            text: constants.treatmentType.surgical,
            value: constants.treatmentType.surgical,
        },
        {
            key: constants.treatmentType.conservative,
            text: constants.treatmentType.conservative,
            value: constants.treatmentType.conservative,
        },
    ];

    const clientTypeOptions = [
        {
            key: constants.ClientType.tpa,
            text: constants.ClientType.tpa,
            value: constants.ClientType.tpa,
        },
        {
            key: constants.ClientType.company,
            text: constants.ClientType.company,
            value: constants.ClientType.company,
        },
    ];

    const searchClients = async (
        type: string,
        isPrimaryClient: boolean
    ) => {
        if (
            isPrimaryClient &&
            !caseTypeName.trim()
        ) {
            toast.error("Please enter Case Type Name.");
            return;
        }

        const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);

        if (!token) {
            toast.error("Authentication token is missing.");
            return;
        }

        try {
            setLoading(true);

            const requestBody: {
                clientType: string;
                caseTypeName?: string;
            } = {
                clientType: type,
            };

            // Search by case type only for the initial TPA/company search.
            // When fetching companies for a selected TPA, fetch all companies.
            if (isPrimaryClient) {
                requestBody.caseTypeName = caseTypeName.trim();
            }

            const response = await fetch(
                `${config.apiBaseURL}/client/find/caseType`,
                {
                    method: "POST",
                    headers: {
                        "x-access-token": token,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            const result: ClientApiResponse = await response.json();

            if (!response.ok || !result.success) {
                toast.error(result.message || "Failed to fetch clients.");

                if (isPrimaryClient) {
                    setClients([]);
                    setClientIds([]);
                } else {
                    setTpaCompany([]);
                    setTpaCompanyIds([]);
                }

                return;
            }

            const items: Client[] = result.data?.items ?? [];

            if (isPrimaryClient) {
                setClients(items);
                setClientIds([]);

                if (type === constants.ClientType.tpa) {
                    setTpaCompany([]);
                    setTpaCompanyIds([]);
                }
            } else {
                setTpaCompany(items);
                setTpaCompanyIds([]);
            }

            if (items.length === 0) {
                toast.error("No clients found.");
                return;
            }

            toast.success(result.message || "Clients found.");
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Failed to fetch clients."
            );

            if (isPrimaryClient) {
                setClients([]);
                setClientIds([]);
            } else {
                setTpaCompany([]);
                setTpaCompanyIds([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCheckbox = (id: string) => {
        if (clientType === constants.ClientType.tpa) {
            if (clientIds.includes(id)) {
                setClientIds([]);
                setTpaCompany([]);
                setTpaCompanyIds([]);
                return;
            }

            if (clientIds.length > 0) {
                toast.error("Only one TPA can be selected at a time.");
                return;
            }

            setClientIds([id]);

            // Fetch all companies without sending caseTypeName.
            void searchClients(constants.ClientType.company, false);
            return;
        }

        setClientIds((previous) =>
            previous.includes(id)
                ? previous.filter((item) => item !== id)
                : [...previous, id]
        );
    };

    const handleTpaCompanyCheckbox = (id: string) => {
        setTpaCompanyIds((previous) =>
            previous.includes(id)
                ? previous.filter((item) => item !== id)
                : [...previous, id]
        );
    };

    const uploadFile = async () => {
        if (!file) {
            toast.error("Please select a file for uploading.");
            return;
        }

        if (!caseTypeName.trim()) {
            toast.error("Please enter Case Type Name.");
            return;
        }

        if (!treatmentType) {
            toast.error("Please select Treatment Type.");
            return;
        }

        if (!clientType) {
            toast.error("Please select Client Type.");
            return;
        }

        if (clientIds.length === 0) {
            toast.error(
                clientType === constants.ClientType.tpa
                    ? "Please select a TPA."
                    : "Please select at least one insurance company."
            );
            return;
        }

        if (
            clientType === constants.ClientType.tpa &&
            tpaCompanyIds.length === 0
        ) {
            toast.error("Please select at least one insurance company.");
            return;
        }

        const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);

        if (!token) {
            toast.error("Authentication token is missing.");
            return;
        }

        const selectedClients = clients.filter((client) =>
            clientIds.includes(client._id)
        );

        if (selectedClients.length !== clientIds.length) {
            toast.error("Please search and select your clients again.");
            return;
        }

        const clientsDataToAdd: ClientDataToAdd[] = selectedClients.map(
            (client) => ({
                ClientId: client._id,
                CaseType: client.case_type_id,
            })
        );

        if (clientsDataToAdd.some((client) => !client.CaseType)) {
            toast.error("Case type ID is missing for a selected client.");
            return;
        }

        const payload: UploadPayload = {
            caseTypeName: caseTypeName.trim(),
            treatmentType,
            clientType,
            clientsDataToAdd,
        };

        if (clientType === constants.ClientType.tpa) {
            const selectedCompanies = tpaCompany.filter((company) =>
                tpaCompanyIds.includes(company._id)
            );

            if (selectedCompanies.length !== tpaCompanyIds.length) {
                toast.error(
                    "Please search and select insurance companies again."
                );
                return;
            }

            payload.tpaCompanyIds = selectedCompanies.map(
                (company) => company._id
            );
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "prompt");
        formData.append("clients", JSON.stringify(payload));

        try {
            setLoading(true);

            const response = await fetch(
                `${config.apiBaseURL}/promptAndFormat/addPrompt`,
                {
                    method: "POST",
                    headers: {
                        "x-access-token": token,
                    },
                    body: formData,
                }
            );

            const result = await response.json();

            if (!response.ok || result.success === false) {
                toast.error(result.message || "Prompt upload failed.");
                return;
            }

            toast.success(result.message || "Prompt uploaded successfully.");
            clearForm();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Something went wrong."
            );
        } finally {
            setLoading(false);
        }
    };

    const clearForm = () => {
        setCaseTypeName("");
        setClients([]);
        setTpaCompany([]);
        setClientIds([]);
        setTpaCompanyIds([]);
        setTreatmentType("");
        setClientType("");
        setFile(null);

        if (fileRef.current) {
            fileRef.current.value = "";
        }
    };

    const handleClientTypeChange = (value: string) => {
        setClientType(value);
        setClients([]);
        setTpaCompany([]);
        setClientIds([]);
        setTpaCompanyIds([]);
    };

    return (
        <Page
            title="Report Guidelines Upload"
            description="Search a case type, select the client and treatment types, choose the required clients, and upload the prompt."
        >
            <Segment>
                <Form>
                    <Grid stackable columns={4}>
                        <Grid.Column>
                            <Form.Field>
                                <label>Case Type Name</label>
                                <Input
                                    fluid
                                    placeholder="Enter Case Type Name"
                                    value={caseTypeName}
                                    onChange={(event) =>
                                        setCaseTypeName(event.target.value)
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Client Type</label>
                                <Dropdown
                                    fluid
                                    selection
                                    placeholder="Select Client Type"
                                    options={clientTypeOptions}
                                    value={clientType}
                                    onChange={(_, { value }) =>
                                        handleClientTypeChange(value as string)
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Search Clients</label>
                                <Button
                                    primary
                                    icon="search"
                                    content="Search Clients"
                                    loading={loading}
                                    disabled={loading || !clientType}
                                    onClick={() =>
                                        void searchClients(clientType, true)
                                    }
                                />
                                <Button
                                    basic
                                    icon="undo"
                                    content="Clear"
                                    disabled={loading}
                                    onClick={clearForm}
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Treatment Type</label>
                                <Dropdown
                                    fluid
                                    selection
                                    placeholder="Select Treatment Type"
                                    options={treatmentTypeOptions}
                                    value={treatmentType}
                                    onChange={(_, { value }) =>
                                        setTreatmentType(value as string)
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Choose Prompt File</label>
                                <Input
                                    fluid
                                    type="file"
                                    input={{
                                        ref: fileRef,
                                        accept: "image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx,.txt,.html",
                                    }}
                                    onChange={(event) => {
                                        const input =
                                            event.target as HTMLInputElement;

                                        setFile(input.files?.[0] || null);
                                    }}
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Upload</label>
                                <Button
                                    positive
                                    icon="upload"
                                    content="Upload Prompt"
                                    loading={loading}
                                    disabled={loading}
                                    onClick={() => void uploadFile()}
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    {clients.length > 0 && (
                        <>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "0.75rem",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                }}
                            >
                                Select {clientType}
                            </div>

                            <Grid columns={4} stackable>
                                {clients.map((client) => (
                                    <Grid.Column key={client._id}>
                                        <Form.Checkbox
                                            label={client.name}
                                            checked={clientIds.includes(
                                                client._id
                                            )}
                                            onChange={() =>
                                                handleCheckbox(client._id)
                                            }
                                        />
                                    </Grid.Column>
                                ))}
                            </Grid>
                        </>
                    )}

                    {clientType === constants.ClientType.tpa &&
                        clientIds.length > 0 &&
                        tpaCompany.length > 0 && (
                            <>
                                <div
                                    style={{
                                        marginTop: "1.5rem",
                                        marginBottom: "0.75rem",
                                        fontWeight: 600,
                                        fontSize: "16px",
                                    }}
                                >
                                    Select Insurance Companies
                                </div>

                                <Grid columns={4} stackable>
                                    {tpaCompany.map((company) => (
                                        <Grid.Column key={company._id}>
                                            <Form.Checkbox
                                                label={company.name}
                                                checked={tpaCompanyIds.includes(
                                                    company._id
                                                )}
                                                onChange={() =>
                                                    handleTpaCompanyCheckbox(
                                                        company._id
                                                    )
                                                }
                                            />
                                        </Grid.Column>
                                    ))}
                                </Grid>
                            </>
                        )}
                </Form>
            </Segment>
        </Page>
    );
};

export default BulkUpload;